import { useState, useCallback, useRef } from 'react';

/**
 * Custom hook för att hantera rumsmått i estimat
 *
 * @param {string} currentEstimatId - ID för nuvarande estimat
 * @param {string} currentStatus - Status för estimatet (draft, sent, etc.)
 * @param {function} onUnsavedNotesChange - Callback när osparade anteckningar ändras
 * @returns {object} - State och funktioner för rumsmått
 */
const useRoomMeasurements = (currentEstimatId, currentStatus, onUnsavedNotesChange) => {
  const [roomMeasurements, setRoomMeasurements] = useState([]);
  const [focusRoomId, setFocusRoomId] = useState(null);

  // Ref för att alltid ha senaste roomMeasurements i save-funktionen
  const roomMeasurementsRef = useRef(roomMeasurements);
  roomMeasurementsRef.current = roomMeasurements;

  const parseNum = (v) => parseFloat(String(v).replace(',', '.')) || 0;

  /**
   * Sparar rumsmått till backend (påverkar inte version)
   * Använder ref för att alltid ha senaste data
   */
  const saveRoomMeasurements = useCallback(async () => {
    if (!currentEstimatId) return;

    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

      const response = await fetch(`${API_URL}/api/estimat/${currentEstimatId}/room-measurements`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          roomMeasurements: roomMeasurementsRef.current.map(room => ({
            ...room,
            floorArea: parseNum(room.floorArea),
            ceilingHeight: parseNum(room.ceilingHeight),
          }))
        }),
      });

      if (!response.ok) {
        console.error('❌ Rumsmått sparning misslyckades:', response.status);
      }
    } catch (error) {
      console.error('❌ Rumsmått sparning fel:', error);
    }
  }, [currentEstimatId]);

  /**
   * Hanterar sparning av rumsmått (on blur)
   */
  const handleRoomMeasurementSave = useCallback(async () => {
    if (!currentEstimatId) {
      onUnsavedNotesChange(true);
      return;
    }
    await saveRoomMeasurements();
    onUnsavedNotesChange(false);
  }, [currentEstimatId, saveRoomMeasurements, onUnsavedNotesChange]);

  /**
   * Lägger till ett nytt rum
   * @param {string} predefinedName - Förifyllt rumsnamn (t.ex. "Sovrum", "Kök")
   */
  const addNewRoom = useCallback((predefinedName = '') => {
    const newId = Date.now();

    // Hantera automatisk numrering för standardrum
    let finalName = predefinedName;
    if (predefinedName) {
      // Räkna hur många rum med samma basnamn som redan finns
      const existingCount = roomMeasurements.filter(room => {
        // Matcha både "Sovrum" och "Sovrum 2", "Sovrum 3" etc.
        return room.name === predefinedName || room.name.startsWith(predefinedName + ' ');
      }).length;

      // Om det redan finns rum med detta namn, lägg till nummer
      if (existingCount > 0) {
        finalName = `${predefinedName} ${existingCount + 1}`;
      }
    }

    // Ärv takhöjden från första rummet med satt värde — vanligtvis är taket
    // samma höjd genom hela bostaden så detta sparar tid. BK kan ändå ändra
    // i enskilda rum där det skiljer sig.
    const inheritedCeilingHeight = (() => {
      const firstWithHeight = roomMeasurements.find(room => {
        const h = parseNum(room.ceilingHeight);
        return h > 0;
      });
      return firstWithHeight ? firstWithHeight.ceilingHeight : '';
    })();

    const newRoom = {
      id: newId,
      name: finalName,
      floorArea: '',
      ceilingHeight: inheritedCeilingHeight,
      wallArea: 0,
      perimeter: 0,
      isEditing: predefinedName === '' // Endast redigera namnet om det inte är förifyllt
    };
    setRoomMeasurements(prev => [...prev, newRoom]);
    setFocusRoomId(newId);
  }, [roomMeasurements]);

  /**
   * Uppdaterar ett fält i ett rum
   * @param {number} id - Rummets ID
   * @param {string} field - Fältnamn att uppdatera
   * @param {any} value - Nytt värde
   */
  const updateRoomField = useCallback((id, field, value) => {
    // Hantera speciell _save signal
    if (field === '_save') {
      handleRoomMeasurementSave();
      return;
    }

    setRoomMeasurements(prev => prev.map(room => {
      if (room.id === id) {
        const updatedRoom = { ...room, [field]: value };

        // Beräkna väggyta och löpmeter när golvyta eller takhöjd ändras
        if (field === 'floorArea' || field === 'ceilingHeight') {
          const floorArea = parseNum(field === 'floorArea' ? value : room.floorArea);
          const ceilingHeight = parseNum(field === 'ceilingHeight' ? value : room.ceilingHeight);

          if (floorArea > 0 && ceilingHeight > 0) {
            updatedRoom.wallArea = Math.ceil(Math.sqrt(floorArea) * 4 * ceilingHeight);
            updatedRoom.perimeter = Math.ceil(4 * Math.sqrt(floorArea));
          } else {
            updatedRoom.wallArea = 0;
            updatedRoom.perimeter = 0;
          }
        }

        return updatedRoom;
      }
      return room;
    }));
  }, [handleRoomMeasurementSave]);

  /**
   * Tar bort ett rum
   * @param {number} id - Rummets ID
   */
  const deleteRoom = useCallback(async (id) => {
    const updatedRooms = roomMeasurements.filter(room => room.id !== id);
    setRoomMeasurements(updatedRooms);
    if (!currentEstimatId) return;
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      await fetch(`${API_URL}/api/estimat/${currentEstimatId}/room-measurements`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ roomMeasurements: updatedRooms })
      });
      onUnsavedNotesChange(false);
    } catch (error) {
      console.error('❌ Radering av rum fel:', error);
    }
  }, [roomMeasurements, currentEstimatId, onUnsavedNotesChange]);

  /**
   * Sätter ny ordning på rummen efter att korten dragits om.
   *
   * Alla rum får ett nytt sortOrder utifrån den ordning de visas i, inte bara
   * de som flyttades — annars blandas manuellt satta värden med rum som ännu
   * bara har sin skapandeordning, och listan hoppar.
   *
   * @param {Array} ordnadeIds - Rummens id i den ordning de ska ligga
   */
  const reorderRooms = useCallback(async (ordnadeIds) => {
    const position = new Map(ordnadeIds.map((id, i) => [String(id), i]));

    const uppdaterade = roomMeasurements.map(room => ({
      ...room,
      sortOrder: position.has(String(room.id))
        ? position.get(String(room.id))
        : (room.sortOrder ?? null)
    }));

    setRoomMeasurements(uppdaterade);

    if (!currentEstimatId) return;
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      await fetch(`${API_URL}/api/estimat/${currentEstimatId}/room-measurements`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ roomMeasurements: uppdaterade })
      });
      onUnsavedNotesChange(false);
    } catch (error) {
      console.error('❌ Omsortering av rum fel:', error);
    }
  }, [roomMeasurements, currentEstimatId, onUnsavedNotesChange]);

  /**
   * Togglar redigeringsläge för ett rum
   * @param {number} id - Rummets ID
   */
  const toggleRoomEdit = useCallback((id) => {
    setRoomMeasurements(prev => prev.map(room =>
      room.id === id ? { ...room, isEditing: !room.isEditing } : room
    ));
    // Ingen automatisk sparning här - bara toggle av redigeringsläge
  }, []);

  /**
   * Rensar fokus-ID efter att fokus har satts
   */
  const clearFocusRoomId = useCallback(() => {
    setFocusRoomId(null);
  }, []);

  return {
    // State
    roomMeasurements,
    focusRoomId,

    // Setters (för initial data load)
    setRoomMeasurements,
    setFocusRoomId,

    // Actions
    addNewRoom,
    updateRoomField,
    reorderRooms,
    deleteRoom,
    toggleRoomEdit,
    clearFocusRoomId,
    saveRoomMeasurements, // Exportera för manuell användning vid behov
  };
};

export default useRoomMeasurements;
